import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  MarketingGlampointsAction,
  MarketingGlampointsBatchResponse,
  MarketingGlampointsService
} from 'src/app/services/marketing-glampoints.service';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-glampoints-action-credit',
  templateUrl: './glampoints-action-credit.component.html',
  styleUrl: './glampoints-action-credit.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [ConfirmationService]
})
export class GlampointsActionCreditComponent implements OnInit {
  private service = inject(MarketingGlampointsService);
  private confirmationService = inject(ConfirmationService);
  private title = inject(Title);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  actionId?: number;
  action: MarketingGlampointsAction | null = null;

  canSubmit = false;
  canValidate = false;
  creditLabel = 'Creditar glampoints';
  validEmails: string[] = [];
  invalidEmails: string[] = [];

  // Resultado da pré-validação no backend (dry-run). Qualquer edição no textarea o invalida.
  preview: MarketingGlampointsBatchResponse | null = null;
  eligibleEmails: string[] = [];
  previewNotFound: string[] = [];
  previewNoSubscription: string[] = [];
  previewDuplicated: string[] = [];
  previewErrors: string[] = [];

  form = new FormGroup({
    emails: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Creditar Glampoints');
    const id = this.route.snapshot.paramMap.get('actionId');
    if (id) {
      this.actionId = Number(id);
      await this.loadAction();
    }
  }

  get inactive(): boolean {
    return !!this.action && this.action.status !== 'ACTIVE';
  }

  async loadAction(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.action = await lastValueFrom(this.service.getAction(this.actionId!));
      this.updateCanSubmit();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  invalidControl(control: string): boolean {
    return (
      this.form.controls[control].invalid &&
      (this.form.controls[control].dirty || this.form.controls[control].touched)
    );
  }

  parseEmails(): void {
    const raw = this.form.controls.emails.value || '';
    const tokens = raw
      .split(/[,;\n\r\t ]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const token of tokens) {
      const key = token.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      if (EMAIL_REGEX.test(token)) valid.push(token);
      else invalid.push(token);
    }
    this.validEmails = valid;
    this.invalidEmails = invalid;
    // Qualquer mudança na lista invalida a pré-validação anterior.
    this.clearPreview();
    this.updateCanSubmit();
  }

  private clearPreview(): void {
    this.preview = null;
    this.eligibleEmails = [];
    this.previewNotFound = [];
    this.previewNoSubscription = [];
    this.previewDuplicated = [];
    this.previewErrors = [];
  }

  /** Pré-valida os e-mails no backend (dry-run) — nada é creditado aqui. */
  async validateEmails(): Promise<void> {
    if (!this.actionId || !this.canValidate) return;
    LoaderService.showLoader();
    try {
      const preview = await lastValueFrom(
        this.service.previewCredit(this.actionId, { emails: this.validEmails })
      );
      this.preview = preview;
      const byStatus = (status: string) =>
        preview.results.filter((r) => r.status === status).map((r) => r.email);
      this.eligibleEmails = byStatus('ELIGIBLE');
      this.previewNotFound = byStatus('NOT_FOUND');
      this.previewNoSubscription = byStatus('NO_GLAM_SUBSCRIPTION');
      this.previewDuplicated = byStatus('DUPLICATE');
      this.previewErrors = byStatus('ERROR');
      this.updateCanSubmit();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  updateCanSubmit(): void {
    this.canValidate = !this.inactive && this.validEmails.length > 0;
    this.canSubmit =
      !!this.action &&
      this.action.status === 'ACTIVE' &&
      this.form.valid &&
      !!this.preview &&
      this.eligibleEmails.length > 0;
    this.creditLabel = this.eligibleEmails.length
      ? `Creditar glampoints (${this.eligibleEmails.length})`
      : 'Creditar glampoints';
  }

  confirmCredit(): void {
    if (!this.canSubmit || !this.action) return;
    const skipped =
      this.previewNotFound.length +
      this.previewNoSubscription.length +
      this.previewDuplicated.length +
      this.previewErrors.length;
    const skippedNote =
      skipped > 0 ? ` ${skipped} e-mail(s) com pendência ficarão de fora.` : '';
    this.confirmationService.confirm({
      header: 'Confirmar crédito de glampoints',
      message:
        `Creditar ${this.action.points} glampoints para ${this.eligibleEmails.length} ` +
        `e-mail(s) validado(s) na ação "${this.action.name}"?${skippedNote}`,
      acceptLabel: 'Creditar',
      rejectLabel: 'Cancelar',
      accept: () => this.submit()
    });
  }

  private async submit(): Promise<void> {
    if (!this.actionId || !this.eligibleEmails.length) return;
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.service.creditAction(this.actionId, { emails: this.eligibleEmails })
      );
      AppDialogService.showInfoDialog({
        header: 'Concluído',
        message:
          `${result.succeeded} creditado(s), ${result.duplicated} duplicado(s), ` +
          `${result.notFound} não encontrado(s), ${result.noGlamSubscription} sem assinatura, ` +
          `${result.failed} falha(s). A ação foi marcada como gerada.`
      });
      await this.router.navigate(['/marketing/glampoints-actions']);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
